/**
 * Popup "Extend days" — tab Customers, Flow Ultra (admin).
 *
 * Admin tanda pelanggan → Extend days → isi hari + sebab → pratonton tarikh
 * lama → baharu → Extend. Kerja sebenar di server (flowExtendService →
 * functions/flowExtendDays.js).
 *
 * ⚠️ KEMBAR: peraturan pratonton (aktif = dari tarikh tamat; tamat = dari hari
 *    ini; bundar 23:59 MY) disalin dari functions/flowExtendDays.js. Pratonton
 *    SAHAJA — tarikh sebenar ialah yang dipulangkan server dan dipaparkan
 *    selepas Extend.
 *
 * Mod "Set date" (2026-09-30): tetapkan tarikh tamat terus — dibuka dari butang
 * "Change" pada Expires dalam popup pelanggan (mode='set'), atau tukar mod di sini.
 * Penyewa Jenis 2: server turut mengemas kini tarikh akses Gmail (flowT2_akses).
 *
 * ⭐ "PAID RENEWAL" (2026-10-14, arahan user): tick → RM + tarikh bayar → server merekod resit
 *    manual (orders, provider 'manual') SERENTAK dengan tambah hari pada langganan yang SAMA —
 *    Dashboard "Renew Flow", tab Orders dan History popup terus mengira. Kes: pelanggan bayar
 *    transfer selepas tamat; Add customer ditolak (rekod ada) dan Extend sahaja tidak merekod duit.
 *    Dibuka juga dari borang Add customer ("Record as a renewal instead") dengan mode='paid' +
 *    bayaranAwal (RM + tarikh dari borang). Kotak: FlowRenewBayarField.jsx · semakan:
 *    utils/flowBayaranManual.js · server: functions/flowResitManual.js.
 */
import { useState } from 'react';
import { X } from 'lucide-react';
import { Btn, IconBtn } from '../design/ui';
import { lanjutHariFlow, setTarikhFlow } from '../services/flowExtendService';
import { produkUntuk, rmDariSen } from '../services/flowProdukService';
import { semakBayaranManual, hariIniMY } from '../utils/flowBayaranManual';
import FlowRenewBayarField from './FlowRenewBayarField';

const DAY_MS = 24 * 60 * 60 * 1000;
const SEBAB_BAYAR = 'Renewal — paid by bank transfer';

// Slot Gmail Open Flow selepas Extend (2026-10-08) — server: functions/flowSlot.js rancangSlotSambung.
// Pulang { teks, amaran } atau null (tiada apa perlu dikatakan).
const slotNota = (r) => {
    const g = r.slotGmail || 'this Gmail';
    if (r.slot === 'tempah') return r.slotGmailMati
        ? { amaran: true, teks: `Open Flow: ${g} is disabled and no other Gmail has a free slot, so this customer still cannot use Flow. Add room on another Gmail, then move them in Session Access.` }
        : r.slotPindahDari !== undefined
            ? { amaran: false, teks: `Open Flow: moved to ${g}${r.slotPindahDari ? ` (${r.slotPindahDari} is disabled or full)` : ''}.` }
            : { amaran: false, teks: `Open Flow slot taken again on ${g}.` };
    if (r.slot === 'penuh') return { amaran: true, teks: `Open Flow: ${g} is full, so no slot was taken. Move this customer to another Gmail in Session Access, or raise its Max users.` };
    if (r.slot === 'menunggu') return { amaran: true, teks: 'Open Flow: this customer is waiting for a Gmail. Assign them in Session Access.' };
    if (['bercanggah', 'gmail-tiada', 'kaunter-rosak'].includes(r.slot)) return { amaran: true, teks: 'Open Flow: no slot was taken because the Gmail records do not match. Check this customer in Session Access.' };
    return null;
};

const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-MY', {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kuala_Lumpur',
}) : '—');

const tarikhBaru = (expiresAt, days) => {
    if (!expiresAt || !days) return null;
    const now = Date.now();
    const cur = new Date(expiresAt).getTime();
    return new Date(Math.max(cur, now) + days * DAY_MS);
};

// 'YYYY-MM-DD' ikut hari Malaysia.
const isoMY = (d) => (d ? new Date(d).toLocaleDateString('en-CA', { timeZone: 'Asia/Kuala_Lumpur' }) : '');

export default function FlowExtendDaysModal({ customers, onClose, onDone, mode: modeAwal = 'add', produk = [], bayaranAwal = null }) {
    // mode 'paid' = mod tambah hari dengan "Paid renewal" sudah ditick (dari borang Add customer).
    const modPaidAwal = modeAwal === 'paid';
    // Lalai hari / RM diambil dari produk pelanggan PERTAMA — semuanya masih boleh ditukar.
    const produkPertama = customers.length ? produkUntuk(customers[0], produk) : null;
    const [mode, setMode] = useState(modPaidAwal ? 'add' : modeAwal);
    const [tarikhTeks, setTarikhTeks] = useState(customers.length === 1 ? isoMY(customers[0].expiresAt) : '');
    const [hariTeks, setHariTeks] = useState(modPaidAwal && produkPertama?.days ? String(produkPertama.days) : '');
    const [sebab, setSebab] = useState(modPaidAwal ? SEBAB_BAYAR : '');
    const [bayarAktif, setBayarAktif] = useState(modPaidAwal);
    const [rmTeks, setRmTeks] = useState(
        bayaranAwal?.rm != null && String(bayaranAwal.rm).trim() !== '' ? String(bayaranAwal.rm)
            : (modPaidAwal && produkPertama?.priceSen ? String(produkPertama.priceSen / 100) : ''),
    );
    const [bayarTarikh, setBayarTarikh] = useState(bayaranAwal?.tarikh || hariIniMY());
    const [sedang, setSedang] = useState(false);
    const [ralat, setRalat] = useState('');
    const [hasil, setHasil] = useState(null); // { results, ok, failed } selepas Extend

    const days = /^\d+$/.test(hariTeks.trim()) ? Number(hariTeks.trim()) : 0;
    const hariSah = days >= 1 && days <= 365;
    const sebabSah = sebab.trim().length >= 3;
    const modSet = mode === 'set';
    // Bayaran hanya dalam mod tambah hari (Set exact date tidak merekod duit — server menolak gabungan itu).
    const bayar = !modSet && bayarAktif;
    const tarikhSah = /^\d{4}-\d{2}-\d{2}$/.test(tarikhTeks);
    const nilaiSah = modSet ? tarikhSah : hariSah;
    // Mod Set date boleh menetapkan tarikh walau tiada tarikh lama; mod tambah hari tidak.
    const tiadaTarikh = modSet ? [] : customers.filter((c) => !c.expiresAt);
    const bilangan = customers.length - tiadaTarikh.length;

    const tukarBayar = (on) => {
        setBayarAktif(on);
        if (!on) return;
        if (!hariTeks.trim() && produkPertama?.days) setHariTeks(String(produkPertama.days));
        if (!rmTeks.trim() && produkPertama?.priceSen) setRmTeks(String(produkPertama.priceSen / 100));
        if (!sebab.trim()) setSebab(SEBAB_BAYAR);
    };

    const hantar = async () => {
        setRalat('');
        if (!modSet && !hariSah) { setRalat('Enter a whole number of days from 1 to 365.'); return; }
        if (modSet && !tarikhSah) { setRalat('Pick the new expiry date.'); return; }
        if (!sebabSah) { setRalat('Write a short reason (at least 3 characters) — it is saved in the log.'); return; }
        let bayaran = null;
        if (bayar) {
            const s = semakBayaranManual({ rmTeks, tarikhTeks: bayarTarikh });
            if (!s.ok) { setRalat(s.ralat); return; }
            bayaran = { paidSen: s.paidSen, paidAt: s.paidAt };
        }
        setSedang(true);
        try {
            const r = modSet
                ? await setTarikhFlow(customers.map((c) => ({ kind: c.kind === 'tenant' ? 'tenant' : 'sub', id: c.id })), tarikhTeks, sebab.trim())
                : await lanjutHariFlow(
                    customers.filter((c) => c.expiresAt).map((c) => ({ kind: c.kind === 'tenant' ? 'tenant' : 'sub', id: c.id })),
                    days, sebab.trim(), bayaran,
                );
            setHasil(r);
            onDone?.(r);
        } catch (e) {
            // Kegagalan paling mungkin: fungsi server belum dideploy. Katakan terus.
            setRalat(e?.code === 'functions/not-found'
                ? 'The server function is not deployed yet — nothing was changed.'
                : `Nothing was changed: ${e?.message || 'unknown error'}`);
        } finally {
            setSedang(false);
        }
    };

    const namaOf = (id) => {
        const c = customers.find((x) => x.id === id);
        return c ? (c.name || c.phone || c.email) : id;
    };

    const tajuk = bayar ? 'Record renewal' : modSet ? 'Change expiry date' : 'Extend days';
    const resitDirekod = hasil ? hasil.results.filter((r) => r.ok && r.orderId).length : 0;

    return (
        <div className="fu-modal-bg" onClick={() => !sedang && onClose()}>
            <div className="fu-modal" onClick={(e) => e.stopPropagation()}>
                <div className="fu-modal__h">
                    <div>
                        <p className="s-cardname">{tajuk}</p>
                        <p className="s-dim">
                            {customers.length} customer{customers.length === 1 ? '' : 's'} selected.
                            {modSet ? ' The expiry is set to exactly the date you pick.'
                                : bayar ? ' Days are added to the same subscription and a receipt is recorded for each customer.'
                                    : ' Active customers get the days added to their current expiry; expired ones count from today.'}
                        </p>
                    </div>
                    <IconBtn label="Close" icon={<X size={15} />} onClick={() => !sedang && onClose()} />
                </div>

                <div className="fu-modal__b">
                    {!hasil && (
                        <>
                            <div style={{ display: 'flex', gap: 6 }}>
                                <Btn size="sm" variant={modSet ? 'ghost' : 'primary'} onClick={() => setMode('add')}>Add days</Btn>
                                <Btn size="sm" variant={modSet ? 'primary' : 'ghost'} onClick={() => setMode('set')}>Set exact date</Btn>
                            </div>
                            <div className="s-kv">
                                {modSet ? (
                                    <div>
                                        <p className="s-kv__lab">New expiry date</p>
                                        <input className="s-inp" type="date"
                                            value={tarikhTeks} onChange={(e) => setTarikhTeks(e.target.value)} />
                                    </div>
                                ) : (
                                    <div>
                                        <p className="s-kv__lab">Days to add</p>
                                        <input className="s-inp" inputMode="numeric" placeholder={produkPertama?.days ? `e.g. ${produkPertama.days}` : 'e.g. 7'}
                                            value={hariTeks} onChange={(e) => setHariTeks(e.target.value)} />
                                    </div>
                                )}
                                <div>
                                    <p className="s-kv__lab">Reason</p>
                                    <input className="s-inp" placeholder="e.g. Lite LP compensation" maxLength={200}
                                        value={sebab} onChange={(e) => setSebab(e.target.value)} />
                                </div>
                            </div>

                            {!modSet && (
                                <FlowRenewBayarField aktif={bayarAktif} onAktif={tukarBayar}
                                    rm={rmTeks} onRm={setRmTeks} tarikh={bayarTarikh} onTarikh={setBayarTarikh}
                                    produkNama={produkPertama?.name} produkSen={produkPertama?.priceSen} disabled={sedang} />
                            )}

                            <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid var(--s-line)', borderRadius: 10 }}>
                                <table className="fu-tbl" style={{ margin: 0 }}>
                                    <thead>
                                        <tr><th>Customer</th><th>Expires now</th><th>New expiry</th></tr>
                                    </thead>
                                    <tbody>
                                        {customers.map((c) => (
                                            <tr key={c.id}>
                                                <td>{c.name || c.phone || c.email}</td>
                                                <td className="s-num">{fmt(c.expiresAt)}</td>
                                                <td className="s-num">
                                                    {modSet ? (tarikhSah ? <b>{fmt(new Date(`${tarikhTeks}T12:00:00+08:00`))}</b> : '—')
                                                        : !c.expiresAt ? <span style={{ color: 'var(--s-stop)' }}>No expiry — will be skipped</span>
                                                        : hariSah ? <b>{fmt(tarikhBaru(c.expiresAt, days))}</b> : '—'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {bayar && customers.length > 1 && (
                                <p className="s-kv__hint">
                                    Each selected customer gets their own receipt of this amount.
                                </p>
                            )}

                            {customers.some((c) => c.jenis === 2) && (
                                <p className="s-kv__hint">
                                    Type 2 customers: their Gmail access end date is changed to match.
                                </p>
                            )}

                            {tiadaTarikh.length > 0 && (
                                <p className="s-kv__hint">
                                    {tiadaTarikh.length} selected customer{tiadaTarikh.length === 1 ? ' has' : 's have'} no expiry
                                    date on record and will not be changed.
                                </p>
                            )}
                        </>
                    )}

                    {hasil && (
                        <>
                            <p style={{ margin: 0 }}>
                                <b>{hasil.ok}</b> {modSet ? 'updated' : <>extended by {days} day{days === 1 ? '' : 's'}</>}
                                {resitDirekod > 0 && <> · <b>{resitDirekod}</b> receipt{resitDirekod === 1 ? '' : 's'} recorded</>}.
                                {hasil.failed > 0 && <> <b style={{ color: 'var(--s-stop)' }}>{hasil.failed} failed.</b></>}
                            </p>
                            <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid var(--s-line)', borderRadius: 10 }}>
                                <table className="fu-tbl" style={{ margin: 0 }}>
                                    <thead>
                                        <tr><th>Customer</th><th>Result</th></tr>
                                    </thead>
                                    <tbody>
                                        {hasil.results.map((r) => (
                                            <tr key={`${r.kind}-${r.id}`}>
                                                <td>{namaOf(r.id)}</td>
                                                <td className="s-num">
                                                    {r.ok ? <>
                                                        {fmt(r.from)} → <b>{fmt(r.to)}</b>
                                                        {r.orderId && (
                                                            <span className="s-dim" style={{ display: 'block' }}>
                                                                Receipt {rmDariSen(r.paidSen || 0)} recorded — see Orders and the Dashboard.
                                                            </span>
                                                        )}
                                                        {/* Akses Open Flow tidak dijumpai — tarikh senarai berubah, tetapi
                                                            extension pelanggan mungkin masih ikut tarikh lama. Beritahu, jangan senyap. */}
                                                        {(r.sessionAccess === 'unknown' || r.sessionAccess === 'ambiguous') && (
                                                            <span className="s-dim" style={{ display: 'block', color: 'var(--s-warn)' }}>
                                                                {r.sessionAccess === 'ambiguous'
                                                                    ? 'Open Flow access not updated — more than one SESAAT account has this WhatsApp number. Save the customer\'s email, then extend again.'
                                                                    : 'Open Flow access not updated — no SESAAT account matches this customer\'s email or WhatsApp number. If they use Open Flow, save their email and extend again.'}
                                                            </span>
                                                        )}
                                                        {slotNota(r) && (
                                                            <span className="s-dim" style={{ display: 'block', ...(slotNota(r).amaran ? { color: 'var(--s-warn)' } : {}) }}>
                                                                {slotNota(r).teks}
                                                            </span>
                                                        )}
                                                    </>
                                                        : <span style={{ color: 'var(--s-stop)' }}>{r.error}</span>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}

                    {ralat && <p style={{ color: 'var(--s-stop)', fontSize: 13, margin: 0 }}>{ralat}</p>}
                </div>

                <div className="fu-modal__f">
                    {hasil ? (
                        <Btn onClick={onClose}>Done</Btn>
                    ) : (
                        <>
                            <Btn variant="ghost" onClick={onClose} disabled={sedang}>Cancel</Btn>
                            <Btn onClick={hantar} disabled={sedang || !nilaiSah || !sebabSah || bilangan === 0}>
                                {sedang ? 'Saving…'
                                    : `${bayar ? 'Record renewal for' : modSet ? 'Set date for' : 'Extend'} ${bilangan} customer${bilangan === 1 ? '' : 's'}`}
                            </Btn>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
